import type {
  PartnerApiToken,
  PartnerStore,
  PartnerUser,
} from "@redotech/admin-sdk/rpc/schema/partners/shared";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import KeyIcon from "@redotech/redo-icons/key-01.svg";
import UserPlus01Icon from "@redotech/redo-icons/user-plus-01.svg";
import { toast } from "@redotech/redo-web/alert";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoEmptyState } from "@redotech/redo-web/arbiter-components/empty-state/redo-empty-state";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import {
  RedoTable,
  type RedoTableColumn,
  RedoTableEmptyStateContent,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import { RedoTableCellLink } from "@redotech/redo-web/arbiter-components/tables/redo-table-cell-link";
import {
  RedoTablePaddedCell,
  RedoTableTextCell,
  RedoTableToolbarCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { StandardRedoTableHeaderCell } from "@redotech/redo-web/arbiter-components/tables/redo-table-header-cells";
import {
  type RedoHorizontalTab,
  RedoHorizontalTabs,
} from "@redotech/redo-web/arbiter-components/tabs/redo-horizontal-tabs";
import {
  type BreadcrumbSlot,
  useBreadcrumb,
} from "@redotech/redo-web/breadcrumb";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { clientErrorMessage } from "@redotech/rpc/errors";
import { memo, type ReactNode, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { RedoAdminRpcClientContext } from "../app/redo-admin-rpc-client-provider";
import { AddPartnerUserModal } from "./add-partner-user-modal";
import { CreatePartnerApiTokenModal } from "./create-partner-api-token-modal";
import { createdColumn } from "./created-column";
import { PartnerStatusBadge } from "./partner-status-badge";

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString();
}

const storeColumns: RedoTableColumn<PartnerStore>[] = [
  {
    key: "store",
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="Store" />,
    renderNormalCell: ({ row }) => (
      <RedoTableCellLink to={`/teams/${row.teamId}/general`}>
        <RedoTableTextCell
          fontWeight="medium"
          subtext={row.storeUrl ?? ""}
          text={row.name || row.teamId}
        />
      </RedoTableCellLink>
    ),
  },
  {
    key: "origin",
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="Access" />,
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell
        text={row.origin === "created" ? "Created by partner" : "Granted"}
      />
    ),
  },
  {
    key: "status",
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="Status" />,
    renderNormalCell: ({ row }) => (
      <RedoTablePaddedCell>
        <RedoBadge
          color={row.status === "active" ? "success" : "gray"}
          size="sm"
          text={row.status === "active" ? "Active" : "Revoked"}
        />
      </RedoTablePaddedCell>
    ),
  },
  {
    key: "scopes",
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="Scopes" />,
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell text={row.scopes.join(", ") || "None"} />
    ),
  },
  {
    key: "added",
    renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="Added" />,
    renderNormalCell: ({ row }) => (
      <RedoTableTextCell text={formatDate(row.createdAt)} />
    ),
  },
];

const PARTNER_TAB_KEYS = ["users", "tokens", "stores"] as const;
type PartnerTabKey = (typeof PARTNER_TAB_KEYS)[number];

/** One tab's body: an optional action above its table. */
const PartnerTab = memo(function PartnerTab({
  action,
  children,
}: {
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Flex dir="column" gap="md" w="full">
      {action && (
        <Flex justify="flex-end" w="full">
          {action}
        </Flex>
      )}
      {children}
    </Flex>
  );
});

interface PartnerDetailPageProps {
  breadcrumb: BreadcrumbSlot;
}

export const PartnerDetailPage = memo(function PartnerDetailPage({
  breadcrumb,
}: PartnerDetailPageProps) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const partnerId = useParams<{ partnerId: string }>().partnerId ?? "";
  const [searchParams, setSearchParams] = useSearchParams();
  const [refreshKey, setRefreshKey] = useState(0);
  const refresh = () => setRefreshKey((key) => key + 1);
  const [addingUser, setAddingUser] = useState(false);
  const [confirmingSuspend, setConfirmingSuspend] = useState(false);
  const [creatingToken, setCreatingToken] = useState(false);
  const [revokingToken, setRevokingToken] = useState<
    PartnerApiToken | undefined
  >();

  const detailLoad = useLoad(
    (signal) => rpcClient.getPartner({ partnerId }, { signal }),
    [rpcClient, partnerId, refreshKey],
  );
  useBreadcrumb(breadcrumb, detailLoad.value?.partner.name ?? "");

  const runAction = async (action: () => Promise<unknown>, done: string) => {
    try {
      await action();
      toast(done, { variant: "success" });
      refresh();
    } catch (err) {
      toast(clientErrorMessage(err, "Something went wrong."), {
        variant: "error",
      });
    }
  };

  if (detailLoad.error) {
    return (
      <RedoEmptyState
        subtext="It may have been deleted, or the link is wrong."
        text="Partner not found"
      />
    );
  }
  const detail = detailLoad.value;
  if (!detail) {
    return null;
  }
  const { partner, users, stores, apiTokens } = detail;
  const suspended = partner.status === "suspended";

  const selectedTab: PartnerTabKey =
    PARTNER_TAB_KEYS.find((key) => key === searchParams.get("tab")) ?? "users";
  const setSelectedTab = (key: PartnerTabKey) =>
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        next.set("tab", key);
        return next;
      },
      { replace: true },
    );
  const tabs: RedoHorizontalTab<PartnerTabKey>[] = [
    { key: "users", label: "Portal users", count: users.length },
    { key: "tokens", label: "API tokens", count: apiTokens.length },
    { key: "stores", label: "Stores", count: stores.length },
  ];

  const setPartnerStatus = (status: "active" | "suspended") =>
    runAction(
      () => rpcClient.updatePartnerStatus({ partnerId, status }),
      status === "active" ? "Partner reactivated" : "Partner suspended",
    );

  const userColumns: RedoTableColumn<PartnerUser>[] = [
    {
      key: "email",
      widthMode: { type: "fill", relativeLength: 2 },
      renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="User" />,
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell leading subtext={row.name ?? ""} text={row.email} />
      ),
    },
    {
      key: "status",
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Status" />
      ),
      renderNormalCell: ({ row }) => (
        <RedoTablePaddedCell>
          <PartnerStatusBadge status={row.status} />
        </RedoTablePaddedCell>
      ),
    },
    createdColumn("Added"),
    {
      key: "actions",
      widthMode: { type: "hug" },
      renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="" />,
      renderNormalCell: ({ row }) => (
        <RedoTableToolbarCell
          buttons={[
            {
              hierarchy: "secondary",
              text: row.status === "active" ? "Disable" : "Enable",
              onClick: () =>
                runAction(
                  () =>
                    rpcClient.updatePartnerUserStatus({
                      partnerId,
                      partnerUserId: row.id,
                      status: row.status === "active" ? "disabled" : "active",
                    }),
                  row.status === "active" ? "User disabled" : "User enabled",
                ),
            },
          ]}
        />
      ),
    },
  ];

  const tokenColumns: RedoTableColumn<PartnerApiToken>[] = [
    {
      key: "name",
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Token" />
      ),
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell
          leading
          subtext={`${row.tokenPrefix}••••${row.tokenSuffix}`}
          text={row.name}
        />
      ),
    },
    createdColumn("Created"),
    {
      key: "lastUsed",
      renderHeaderCell: () => (
        <StandardRedoTableHeaderCell headerText="Last used" />
      ),
      renderNormalCell: ({ row }) => (
        <RedoTableTextCell
          text={row.lastUsedAt ? formatDate(row.lastUsedAt) : "Never"}
        />
      ),
    },
    {
      key: "actions",
      widthMode: { type: "hug" },
      renderHeaderCell: () => <StandardRedoTableHeaderCell headerText="" />,
      renderNormalCell: ({ row }) => (
        <RedoTableToolbarCell
          buttons={[
            {
              hierarchy: "secondary",
              text: "Revoke",
              onClick: () => setRevokingToken(row),
            },
          ]}
        />
      ),
    },
  ];

  return (
    <Flex dir="column" gap="xl" w="full">
      <Flex align="center" gap="md" justify="space-between" w="full">
        <Flex align="center" gap="md">
          <PartnerStatusBadge status={partner.status} />
          <Text fontSize="sm" m="none" textColor="tertiary">
            Created {formatDate(partner.createdAt)} by {partner.createdBy}
          </Text>
        </Flex>
        {suspended ? (
          <RedoButton
            hierarchy="primary"
            onClick={() => setPartnerStatus("active")}
            size="sm"
            text="Reactivate"
          />
        ) : (
          <RedoButton
            hierarchy="secondary"
            onClick={() => setConfirmingSuspend(true)}
            size="sm"
            text="Suspend"
          />
        )}
      </Flex>
      <Flex dir="column" w="full">
        <RedoHorizontalTabs
          pl="none"
          selectedTab={selectedTab}
          setSelectedTab={setSelectedTab}
          tabs={tabs}
        />
      </Flex>
      {selectedTab === "users" && (
        <PartnerTab
          action={
            <RedoButton
              hierarchy="secondary"
              IconLeading={UserPlus01Icon}
              onClick={() => setAddingUser(true)}
              size="sm"
              text="Add user"
            />
          }
        >
          <RedoTable
            emptyStateContent={
              <RedoTableEmptyStateContent text="No portal users" />
            }
            layout={{ columns: userColumns, standalone: true }}
            rowsOfData={users}
          />
        </PartnerTab>
      )}
      {selectedTab === "tokens" && (
        <PartnerTab
          action={
            <RedoButton
              hierarchy="secondary"
              IconLeading={KeyIcon}
              onClick={() => setCreatingToken(true)}
              size="sm"
              text="Create token"
            />
          }
        >
          <RedoTable
            emptyStateContent={
              <RedoTableEmptyStateContent text="No API tokens" />
            }
            layout={{ columns: tokenColumns, standalone: true }}
            rowsOfData={apiTokens}
          />
        </PartnerTab>
      )}
      {selectedTab === "stores" && (
        <PartnerTab>
          <RedoTable
            emptyStateContent={<RedoTableEmptyStateContent text="No stores" />}
            layout={{ columns: storeColumns, standalone: true }}
            rowsOfData={stores}
          />
        </PartnerTab>
      )}
      {addingUser && (
        <AddPartnerUserModal
          onAdded={() => {
            setAddingUser(false);
            toast("Portal user added", { variant: "success" });
            refresh();
          }}
          onClose={() => setAddingUser(false)}
          partnerId={partnerId}
          partnerName={partner.name}
        />
      )}
      {creatingToken && (
        <CreatePartnerApiTokenModal
          onClose={() => {
            setCreatingToken(false);
            refresh();
          }}
          partnerId={partnerId}
          partnerName={partner.name}
        />
      )}
      {revokingToken && (
        <RedoModal
          onModalCloseRequested={() => setRevokingToken(undefined)}
          primaryButton={{
            text: "Revoke token",
            theme: "destructive",
            onClick: async () => {
              const tokenId = revokingToken.id;
              setRevokingToken(undefined);
              await runAction(
                () => rpcClient.revokePartnerApiToken({ partnerId, tokenId }),
                "Token revoked",
              );
            },
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setRevokingToken(undefined),
          }}
          subtitle={`Calls that use "${revokingToken.name}" fail right away. Store tokens the partner already issued keep working.`}
          theme="error"
          title="Revoke API token?"
        />
      )}
      {confirmingSuspend && (
        <RedoModal
          onModalCloseRequested={() => setConfirmingSuspend(false)}
          primaryButton={{
            text: "Suspend partner",
            onClick: async () => {
              setConfirmingSuspend(false);
              await setPartnerStatus("suspended");
            },
          }}
          secondaryButton={{
            text: "Cancel",
            onClick: () => setConfirmingSuspend(false),
          }}
          subtitle={`${partner.name}'s API tokens stop working and its portal users are signed out until you reactivate it. Its stores keep running.`}
          title="Suspend partner?"
        />
      )}
    </Flex>
  );
});
